import { Component, inject, ChangeDetectionStrategy } from "@angular/core";
import { DynamicDialogConfig, DynamicDialogRef } from "@openng/optimus-ui/dynamicdialog";
import { Translation } from "../../../../../models/api/fault/fault";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { InputTextModule } from "@openng/optimus-ui/inputtext";
import { DropdownOption } from "../../../../../models/ui/dropdownOption";
import { ButtonComponent } from "../../../../../shared/components/button/button.component";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { URL_PATTERN } from "../../../../../constants/regExp";
import { SvgIconComponent } from "../../../../../shared/components/svg-icon/svg-icon.component";
import { TooltipModule } from "@openng/optimus-ui/tooltip";
import { SelectModule } from "@openng/optimus-ui/select";
import { TextareaModule } from "@openng/optimus-ui/textarea";

@Component({
  selector: "app-translation",
  standalone: true,
  imports: [
    ReactiveFormsModule,
    SelectModule,
    TextareaModule,
    InputTextModule,
    ButtonComponent,
    TranslateModule,
    SvgIconComponent,
    TooltipModule,
  ],
  templateUrl: "./translation.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./translation.component.scss",
})
export class TranslationComponent {
  private config = inject(DynamicDialogConfig);
  public translation: Translation = this.config.data?.translation ?? {};
  private ref = inject(DynamicDialogRef);
  public translate = inject(TranslateService);
  public form = new FormGroup({
    fixLinkLite: new FormControl<string | null>(this.toNullableLink(this.translation.fixLinkLite), [
      Validators.pattern(URL_PATTERN),
    ]),
    fixLinkExpert: new FormControl<string | null>(this.toNullableLink(this.translation.fixLinkExpert), [
      Validators.pattern(URL_PATTERN),
    ]),
    faultText: new FormControl<string>(this.translation.faultText ?? "", {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(1000)],
    }),
    languageCode: new FormControl<string>(this.normalizeLanguageCode(this.translation.languageCode), {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });
  public languages: DropdownOption[] = this.config.data?.languages ?? [];

  /**
   * Saves the translation data to the dialog.
   * The translation data is taken from the form value.
   * If a language code is provided, the corresponding language name is looked up and added to the translation.
   * The dialog is then closed with the updated translation.
   * */
  public save() {
    this.form.controls.languageCode.setValue(this.normalizeLanguageCode(this.form.controls.languageCode.value));
    this.form.controls.faultText.setValue(this.form.controls.faultText.value.trim());
    this.form.controls.fixLinkLite.setValue(this.toNullableLink(this.form.controls.fixLinkLite.value));
    this.form.controls.fixLinkExpert.setValue(this.toNullableLink(this.form.controls.fixLinkExpert.value));

    if (this.form.valid) {
      const formValues = this.form.getRawValue();
      const languageCode = this.normalizeLanguageCode(formValues.languageCode);
      if (!languageCode) {
        this.form.controls.languageCode.setErrors({ required: true });
        return;
      }

      const translation: Translation = {
        ...this.translation,
        languageCode,
        faultText: formValues.faultText.trim(),
        fixLinkLite: this.toNullableLink(formValues.fixLinkLite),
        fixLinkExpert: this.toNullableLink(formValues.fixLinkExpert),
      };
      const languageName = this.findLanguageName(languageCode);
      if (languageName) {
        translation.languageName = languageName;
      } else {
        delete translation.languageName;
      }

      this.ref.close(translation);
    }
  }

  /**
   * Closes the dialog without returning any data.
   */
  public closeDialog() {
    this.ref.close(null);
  }

  /**
   * Finds the label of a language in the languages array by its value.
   * @param code The value of the language to find.
   * @returns The label of the language, or undefined if not found.
   */
  private findLanguageName(code: string) {
    return this.languages.find((language) => this.normalizeLanguageCode(String(language.value)) === code)?.label;
  }

  /**
   * Converts locale-form language values, such as `de-DE`, to the language code
   * accepted by the fault-list API.
   */
  private normalizeLanguageCode(code: string | null | undefined): string {
    return (code ?? "").trim().toLowerCase().split(/[-_]/, 1)[0] ?? "";
  }

  /**
   * Empty optional links are sent explicitly as null, as required by the API.
   */
  private toNullableLink(link: string | null | undefined): string | null {
    const normalizedLink = link?.trim();
    return normalizedLink ? normalizedLink : null;
  }
}
